import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VariablePicker, insertToken } from '@/components/messaging/templateVariables';
import {
  Plus, Trash2, Clock, GitBranch, TestTube, Layers,
  MessageSquare, Mail, Phone, ArrowDown, GripVertical,
  Settings, ChevronDown, ChevronRight, X, Save
} from 'lucide-react';
import { campaignsApi, Engagement, CreateEngagementRequest } from '@/services/campaignsApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { StepOptimalTimingHint } from './ml';

// ============================================================================
// TYPES
// ============================================================================

interface ABVariant {
  variant: string;
  percentage: number;
  content_template: string;
}

interface EngagementStep {
  id: string;
  engagement_id?: string;
  engagement_type: 'sms' | 'email' | 'whatsapp' | 'rcs' | 'survey' | 'chatflow';
  name: string;
  content_template: string;
  channel: string;
  delay_hours: number;
  condition_type: 'always' | 'response_received' | 'no_response' | 'response_match';
  condition_config?: Record<string, any>;
  wait_for_response: boolean;
  timeout_hours: number;
  timeout_action: 'skip' | 'retry' | 'escalate';
  is_ab_test: boolean;
  ab_variants?: ABVariant[];
  is_parallel: boolean;
  channel_content?: Record<string, { content: string; subject?: string }>;
}

interface EngagementSequenceBuilderProps {
  campaignId?: string;
  organizationId: number;
  initialEngagements?: EngagementStep[];
  onSave: (steps: EngagementStep[]) => void;
  availableChannels: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ENGAGEMENT_TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare, color: 'bg-green-50 text-green-700' },
  rcs: { label: 'RCS', icon: Phone, color: 'bg-indigo-100 text-indigo-700' },
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700' },
  survey: { label: 'Survey', icon: Layers, color: 'bg-purple-100 text-purple-700' },
  chatflow: { label: 'Chatflow', icon: GitBranch, color: 'bg-orange-100 text-orange-700' },
};

const CONDITION_LABELS: Record<string, string> = {
  always: 'Always',
  response_received: 'If responded',
  no_response: 'If no response',
  response_match: 'If response matches',
};

function generateId(): string {
  return `step_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function createDefaultStep(availableChannels: string[]): EngagementStep {
  return {
    id: generateId(),
    engagement_type: 'sms',
    name: '',
    content_template: '',
    channel: availableChannels[0] || 'sms',
    delay_hours: 0,
    condition_type: 'always',
    wait_for_response: false,
    timeout_hours: 24,
    timeout_action: 'skip',
    is_ab_test: false,
    ab_variants: [],
    is_parallel: false,
    channel_content: {},
  };
}

// ============================================================================
// SORTABLE STEP CARD
// ============================================================================

interface SortableStepCardProps {
  step: EngagementStep;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableStepCard({ step, index, isEditing, onEdit, onDelete }: SortableStepCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const typeConfig = ENGAGEMENT_TYPE_CONFIG[step.engagement_type];
  const TypeIcon = typeConfig.icon;

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`border rounded-lg bg-white shadow-sm ${
          isEditing ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center gap-3 p-4">
          {/* Drag handle */}
          <button
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-5 h-5" />
          </button>

          {/* Step number */}
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-800 text-white text-sm font-medium flex items-center justify-center">
            {index + 1}
          </div>

          {/* Type icon + label */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-sm font-medium ${typeConfig.color}`}>
            <TypeIcon className="w-4 h-4" />
            {typeConfig.label}
          </div>

          {/* Channel badge */}
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium uppercase">
            {step.channel}
          </span>

          {/* Name + content preview */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {step.name || 'Untitled Step'}
            </p>
            {step.content_template && (
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {step.content_template.slice(0, 80)}
                {step.content_template.length > 80 ? '...' : ''}
              </p>
            )}
          </div>

          {/* Indicators */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {step.condition_type !== 'always' && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-xs">
                <GitBranch className="w-3 h-3" />
                {CONDITION_LABELS[step.condition_type]}
              </span>
            )}
            {step.is_ab_test && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-paper-2 text-ink-500 text-xs">
                <TestTube className="w-3 h-3" />
                A/B
              </span>
            )}
          </div>

          {/* Actions */}
          <button
            onClick={onEdit}
            className="p-1.5 rounded-md text-ink-500 hover:text-ink hover:bg-paper-2 transition-colors"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DELAY CONNECTOR
// ============================================================================

function DelayConnector({ delayHours }: { delayHours: number }) {
  return (
    <div className="flex items-center justify-center py-1">
      <div className="flex flex-col items-center">
        <ArrowDown className="w-4 h-4 text-gray-300" />
        <span className="text-xs text-gray-400 mt-0.5">
          {delayHours > 0 ? (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Wait {delayHours}h
            </span>
          ) : (
            'Immediately'
          )}
        </span>
        <ArrowDown className="w-4 h-4 text-gray-300" />
      </div>
    </div>
  );
}

// ============================================================================
// ADD STEP BUTTON
// ============================================================================

function AddStepButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex justify-center py-1">
      <button
        onClick={onClick}
        className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed border-v2border rounded-lg text-sm text-ink-500 hover:text-ink hover:bg-paper-2 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Step
      </button>
    </div>
  );
}

// ============================================================================
// STEP EDITOR
// ============================================================================

interface StepEditorProps {
  step: EngagementStep;
  availableChannels: string[];
  onChange: (updated: EngagementStep) => void;
  onClose: () => void;
}

function StepEditor({ step, availableChannels, onChange, onClose }: StepEditorProps) {
  const [activeChannelTab, setActiveChannelTab] = useState<string>(step.channel);
  const [showConditionConfig, setShowConditionConfig] = useState(step.condition_type === 'response_match');

  const updateField = <K extends keyof EngagementStep>(key: K, value: EngagementStep[K]) => {
    onChange({ ...step, [key]: value });
  };

  // Variables come from the org's template-variable registry; the campaign
  // sender renders them per member (routers/campaigns.py).
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const insertVariable = (key: string) => {
    updateField('content_template', insertToken(contentRef.current, step.content_template, key));
  };

  const updateVariant = (index: number, updates: Partial<ABVariant>) => {
    const variants = [...(step.ab_variants || [])];
    variants[index] = { ...variants[index], ...updates };
    updateField('ab_variants', variants);
  };

  const addVariant = () => {
    const existing = step.ab_variants || [];
    const remainingPct = Math.max(0, 100 - existing.reduce((sum, v) => sum + v.percentage, 0));
    const newVariant: ABVariant = {
      variant: String.fromCharCode(65 + existing.length),
      percentage: remainingPct,
      content_template: '',
    };
    updateField('ab_variants', [...existing, newVariant]);
  };

  const removeVariant = (index: number) => {
    const variants = [...(step.ab_variants || [])];
    variants.splice(index, 1);
    updateField('ab_variants', variants);
  };

  const updateChannelContent = (channel: string, content: string, subject?: string) => {
    const channelContent = { ...(step.channel_content || {}) };
    channelContent[channel] = { content, subject: subject ?? channelContent[channel]?.subject };
    updateField('channel_content', channelContent);
  };

  const updateChannelSubject = (channel: string, subject: string) => {
    const channelContent = { ...(step.channel_content || {}) };
    channelContent[channel] = { content: channelContent[channel]?.content || '', subject };
    updateField('channel_content', channelContent);
  };

  const addConditionRule = () => {
    const config = { ...(step.condition_config || {}) };
    const key = `rule_${Object.keys(config).length + 1}`;
    config[key] = '';
    updateField('condition_config', config);
  };

  const updateConditionRule = (oldKey: string, newKey: string, value: string) => {
    const config = { ...(step.condition_config || {}) };
    if (oldKey !== newKey) {
      delete config[oldKey];
    }
    config[newKey] = value;
    updateField('condition_config', config);
  };

  const removeConditionRule = (key: string) => {
    const config = { ...(step.condition_config || {}) };
    delete config[key];
    updateField('condition_config', config);
  };

  return (
    <div className="border border-v2border rounded-lg bg-surface p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Edit Step</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Engagement Type Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Engagement Type</label>
        <div className="flex gap-2">
          {(Object.keys(ENGAGEMENT_TYPE_CONFIG) as Array<EngagementStep['engagement_type']>).map((type) => {
            const config = ENGAGEMENT_TYPE_CONFIG[type];
            const Icon = config.icon;
            return (
              <button
                key={type}
                onClick={() => updateField('engagement_type', type)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  step.engagement_type === type
                    ? 'border-v2accent bg-v2accent text-white'
                    : 'border-v2border bg-surface text-ink-500 hover:text-ink'
                }`}
              >
                <Icon className="w-4 h-4" />
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Name */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Step Name</label>
        <input
          type="text"
          value={step.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="e.g., Welcome Message"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Content Template */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Content Template</label>
        <textarea
          ref={contentRef}
          value={step.content_template}
          onChange={(e) => updateField('content_template', e.target.value)}
          rows={4}
          placeholder="Type your message here..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        />
        <div className="mt-1.5">
          <VariablePicker appliesTo="messages" onInsert={insertVariable} />
        </div>
      </div>

      {/* Channel Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Channel</label>
        <select
          value={step.channel}
          onChange={(e) => updateField('channel', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {availableChannels.map((ch) => (
            <option key={ch} value={ch}>
              {ch.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      {/* Multi-Channel Content Tabs */}
      {availableChannels.length > 1 && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Multi-Channel Content</label>
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            <div className="flex border-b border-gray-200">
              {availableChannels.map((ch) => (
                <button
                  key={ch}
                  onClick={() => setActiveChannelTab(ch)}
                  className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                    activeChannelTab === ch
                      ? 'border-v2accent text-ink bg-paper-2'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {ch.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="p-3 space-y-2">
              {activeChannelTab === 'email' && (
                <input
                  type="text"
                  value={step.channel_content?.[activeChannelTab]?.subject || ''}
                  onChange={(e) => updateChannelSubject(activeChannelTab, e.target.value)}
                  placeholder="Email subject line"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
              <textarea
                value={step.channel_content?.[activeChannelTab]?.content || ''}
                onChange={(e) => updateChannelContent(activeChannelTab, e.target.value)}
                rows={3}
                placeholder={`Content for ${activeChannelTab.toUpperCase()} channel...`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              />
            </div>
          </div>
        </div>
      )}

      {/* Channel Override */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Channel Override</label>
        <select
          value={step.channel || ''}
          onChange={(e) => updateField('channel', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Use member&apos;s preferred channel</option>
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="rcs">RCS</option>
          <option value="email">Email</option>
          <option value="voice">Voice</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">Override the default channel for this step</p>
      </div>

      {/* Delay Configuration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Delay Before Step</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={step.delay_hours}
              onChange={(e) => updateField('delay_hours', Math.max(0, parseInt(e.target.value) || 0))}
              disabled={step.delay_hours === 0}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
            />
            <span className="text-sm text-gray-500">hours</span>
          </div>
          <label className="flex items-center gap-2 mt-1.5">
            <input
              type="checkbox"
              checked={step.delay_hours === 0}
              onChange={(e) => updateField('delay_hours', e.target.checked ? 0 : 1)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-600">Send immediately</span>
          </label>
          {/* Phase 5c — send-timing hint (best hour-of-day for replies) */}
          <div className="mt-1.5">
            <StepOptimalTimingHint />
          </div>
        </div>

        {/* Timeout */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Response Timeout</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={step.timeout_hours}
              onChange={(e) => updateField('timeout_hours', Math.max(1, parseInt(e.target.value) || 24))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-500">hours</span>
          </div>
          <div className="mt-1.5">
            <select
              value={step.timeout_action}
              onChange={(e) => updateField('timeout_action', e.target.value as EngagementStep['timeout_action'])}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="skip">On timeout: Skip</option>
              <option value="retry">On timeout: Retry</option>
              <option value="escalate">On timeout: Escalate</option>
            </select>
          </div>
        </div>
      </div>

      {/* Condition Configuration */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Condition</label>
        <select
          value={step.condition_type}
          onChange={(e) => {
            const newType = e.target.value as EngagementStep['condition_type'];
            updateField('condition_type', newType);
            setShowConditionConfig(newType === 'response_match');
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="always">Always</option>
          <option value="response_received">After Response</option>
          <option value="no_response">If No Response</option>
          <option value="response_match">Response Match</option>
        </select>

        {showConditionConfig && (
          <div className="mt-2 space-y-2 p-3 border border-gray-200 rounded-lg bg-white">
            <p className="text-xs text-gray-500 font-medium">Match Rules</p>
            {Object.entries(step.condition_config || {}).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="text"
                  value={key}
                  onChange={(e) => updateConditionRule(key, e.target.value, value as string)}
                  placeholder="Label"
                  className="w-1/3 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-xs text-gray-400">=</span>
                <input
                  type="text"
                  value={value as string}
                  onChange={(e) => updateConditionRule(key, key, e.target.value)}
                  placeholder="Match text"
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => removeConditionRule(key)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              onClick={addConditionRule}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              + Add Rule
            </button>
          </div>
        )}
      </div>

      {/* Wait for Response Toggle */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={step.wait_for_response}
          onChange={(e) => updateField('wait_for_response', e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">Wait for response before proceeding</span>
      </label>

      {/* A/B Test Toggle */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={step.is_ab_test}
            onChange={(e) => {
              updateField('is_ab_test', e.target.checked);
              if (e.target.checked && (!step.ab_variants || step.ab_variants.length === 0)) {
                updateField('ab_variants', [
                  { variant: 'A', percentage: 50, content_template: step.content_template },
                  { variant: 'B', percentage: 50, content_template: '' },
                ]);
              }
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 flex items-center gap-1">
            <TestTube className="w-4 h-4 text-indigo-500" />
            Enable A/B Test
          </span>
        </label>

        {step.is_ab_test && (
          <div className="mt-3 space-y-3 p-3 border border-v2border rounded-lg bg-paper-2">
            {(step.ab_variants || []).map((variant, idx) => (
              <div key={idx} className="space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">Variant {variant.variant}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={variant.percentage}
                      onChange={(e) => updateVariant(idx, { percentage: parseInt(e.target.value) })}
                      className="w-24 h-1.5 accent-indigo-600"
                    />
                    <span className="text-xs font-medium text-gray-600 w-8 text-right">{variant.percentage}%</span>
                    {(step.ab_variants || []).length > 2 && (
                      <button
                        onClick={() => removeVariant(idx)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <textarea
                  value={variant.content_template}
                  onChange={(e) => updateVariant(idx, { content_template: e.target.value })}
                  rows={3}
                  placeholder={`Content for variant ${variant.variant}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                />
              </div>
            ))}
            <button
              onClick={addVariant}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              + Add Variant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SUMMARY BAR
// ============================================================================

function SummaryBar({ steps }: { steps: EngagementStep[] }) {
  const totalDurationHours = steps.reduce((sum, s) => sum + s.delay_hours + (s.wait_for_response ? s.timeout_hours : 0), 0);
  const channelsUsed = Array.from(new Set(steps.map((s) => s.channel)));
  const abTestCount = steps.filter((s) => s.is_ab_test).length;

  const formatDuration = (hours: number): string => {
    if (hours === 0) return 'Immediate';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remaining = hours % 24;
    return remaining > 0 ? `${days}d ${remaining}h` : `${days}d`;
  };

  return (
    <div className="flex items-center gap-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
      <div className="flex items-center gap-1.5 text-gray-600">
        <Layers className="w-4 h-4" />
        <span className="font-medium">{steps.length}</span> steps
      </div>
      <div className="flex items-center gap-1.5 text-gray-600">
        <Clock className="w-4 h-4" />
        Est. <span className="font-medium">{formatDuration(totalDurationHours)}</span>
      </div>
      <div className="flex items-center gap-1.5 text-gray-600">
        <MessageSquare className="w-4 h-4" />
        {channelsUsed.length > 0 ? channelsUsed.map((c) => c.toUpperCase()).join(', ') : 'No channels'}
      </div>
      {abTestCount > 0 && (
        <div className="flex items-center gap-1.5 text-indigo-600">
          <TestTube className="w-4 h-4" />
          <span className="font-medium">{abTestCount}</span> A/B test{abTestCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const EngagementSequenceBuilder: React.FC<EngagementSequenceBuilderProps> = ({
  campaignId,
  organizationId,
  initialEngagements,
  onSave,
  availableChannels,
}) => {
  const [steps, setSteps] = useState<EngagementStep[]>(initialEngagements || []);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (initialEngagements) {
      setSteps(initialEngagements);
    }
  }, [initialEngagements]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setSteps((prev) => {
          const oldIndex = prev.findIndex((s) => s.id === active.id);
          const newIndex = prev.findIndex((s) => s.id === over.id);
          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    },
    []
  );

  const addStep = useCallback(
    (atIndex?: number) => {
      const newStep = createDefaultStep(availableChannels);
      setSteps((prev) => {
        const next = [...prev];
        if (atIndex !== undefined) {
          next.splice(atIndex, 0, newStep);
        } else {
          next.push(newStep);
        }
        return next;
      });
      setEditingStepId(newStep.id);
    },
    [availableChannels]
  );

  const deleteStep = useCallback((stepId: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
    setEditingStepId((current) => (current === stepId ? null : current));
  }, []);

  const updateStep = useCallback((updated: EngagementStep) => {
    setSteps((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const handleSave = () => {
    onSave(steps);
  };

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <SummaryBar steps={steps} />

      {/* Step List */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                {/* Delay connector between steps */}
                {index > 0 && <DelayConnector delayHours={step.delay_hours} />}

                {/* Add step button before this step */}
                {index > 0 && <AddStepButton onClick={() => addStep(index)} />}

                {/* Step card */}
                <SortableStepCard
                  step={step}
                  index={index}
                  isEditing={editingStepId === step.id}
                  onEdit={() => setEditingStepId(editingStepId === step.id ? null : step.id)}
                  onDelete={() => deleteStep(step.id)}
                />

                {/* Inline editor */}
                {editingStepId === step.id && (
                  <div className="mt-2 mb-2">
                    <StepEditor
                      step={step}
                      availableChannels={availableChannels}
                      onChange={updateStep}
                      onClose={() => setEditingStepId(null)}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add step at end */}
      <AddStepButton onClick={() => addStep()} />

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={steps.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-v2accent text-white text-sm font-medium rounded-lg hover:bg-v2accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          Save Sequence
        </button>
      </div>
    </div>
  );
};

export type { EngagementStep, ABVariant, EngagementSequenceBuilderProps };
export default EngagementSequenceBuilder;
